import { ApiException, client } from "@/lib/client";
import { queryClient } from "@/lib/react-query";
import { InvoiceInfo, User } from "@/shared/model/user";
import type { UserResponse } from "@/shared/response";
import { useQuery } from "@tanstack/react-query";

export const userQueryKey = "user";

export const useUserController = ({
  ignoreException = false,
}: { ignoreException?: boolean } = {}) => {
  const { data, isLoading } = useQuery<UserResponse | null>({
    queryKey: [userQueryKey, "me"],
    queryFn: async () => {
      try {
        const response = await client.fetch(`/user`);

        if (!response.ok) {
          throw new ApiException(response.statusText);
        }

        const json = await response.json();

        const data: UserResponse = json.data;

        return data;
      } catch (error) {
        if (ignoreException) {
          return null;
        }

        throw error;
      }
    },
  });

  const patch = async ({
    first_name,
    last_name,
    invoice_info,
    linkedin_url,
    profession,
  }: {
    first_name?: string;
    last_name?: string;
    invoice_info?: InvoiceInfo;
    linkedin_url?: string;
    profession?: string;
  }) => {
    const response = await client.fetch(`/user`, {
      method: "PATCH",
      body: JSON.stringify({
        first_name,
        last_name,
        invoice_info,
        linkedin_url,
        profession,
      }),
    });

    if (!response.ok) {
      throw new ApiException(response.statusText);
    }

    queryClient.invalidateQueries({ queryKey: [userQueryKey, "me"] });
    queryClient.invalidateQueries({ queryKey: [userQueryKey, "all"] });
  }


  return {
    data,
    isLoading,
    patch,
  };
};

export const useAllUsersController = () => {
  const { data, isLoading } = useQuery<User[]>({
    queryKey: [userQueryKey, "all"],
    queryFn: async () => {
      const response = await client.fetch(`/users`);

      if (!response.ok) {
        throw new ApiException(response.statusText);
      }

      const json = await response.json();
      return json.data;
    },
  });

  return {
    data: data || [],
    isLoading,
  };
};
